import React, { useEffect } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { Stack } from '@mui/material'
import { Loader } from 'components'
import { useInternalRouteAccess, useWorkOSAuth } from 'hooks'
import { ROUTES } from 'routes'
import { useVeraSpaceStore } from 'stores'
import { vhMinus } from 'utils/textZoom'

interface InternalRouteProps {
  redirectPath?: string
}

/**
 * Route guard for internal-only routes (Vera employees only).
 *
 * TWO GATES, NOT ONE (VRG-410).
 *
 * 1. IS THIS VERA STAFF? — `useInternalRouteAccess`'s `isInternal`, which reads the one
 *    `INTERNAL_ROLE_NAMES` list rather than a hardcoded pair. It used to spell out
 *    `=== 'platform_admin' || === 'platform_manager'` here, which is how the console
 *    came to exclude two roles the seed had deliberately given internal permissions to:
 *    `vera_fulfillment` held exactly the four permissions the fulfilment pipeline needs
 *    and could not open a single page that uses them.
 *
 * 2. CAN THEY USE *THIS* PAGE? — `canReachInternalPath`, from the route→permission table
 *    in `routes/internalAccess.ts`. Widening gate 1 without gate 2 would only move the
 *    defect: fulfilment would reach `/internal/subscriptions` and 403 on every data
 *    call, which is the same broken promise in the opposite direction.
 *
 * A staffer who fails gate 2 is sent to `/internal/companies` — the console's landing
 * page, and the one page all four internal roles hold `view_companies` for
 * (`seed.ts:565→'view_companies'`, 614, 647, 671) — rather than out to a brand surface
 * they have no company for.
 *
 * The API's `@Permissions` guards remain the real boundary (§27); this keeps someone
 * from landing on a page that can only render errors.
 */
export const InternalRoute = (props: InternalRouteProps) => {
  // VRG-339: `/` decides where a company user lands, so this guard defers to it rather
  // than being a seventh hardcoded copy. It defaulted to ROUTES.CERTIFICATES, which sent
  // a first-run brand straight past Home to an empty table.
  const { redirectPath = ROUTES.HOME } = props
  const { isAuthenticated, isLoading } = useWorkOSAuth()
  // Hooks must run unconditionally (before the early returns below) — Rules of Hooks.
  const inSpace = useVeraSpaceStore((s) => !!s.companyId)
  const { isInternal, canReachInternalPath } = useInternalRouteAccess()
  const { pathname } = useLocation()
  // VRG-369: the destination the caller that entered space asked for, if any. See the
  // `landing` doc in useVeraSpaceStore for why this guard has to know about it.
  const landing = useVeraSpaceStore((s) => s.landing)
  // VRG-677: the way-OUT handoff. Once a staffer is rendering an internal page outside
  // a space, "Return to Internal" has landed and the handoff must not outlive it (a later
  // visit to a company settings URL should bounce to Account, as it always did).
  const leaving = useVeraSpaceStore((s) => s.leaving)
  const clearLeaving = useVeraSpaceStore((s) => s.clearLeaving)
  useEffect(() => {
    if (leaving && isInternal) clearLeaving()
  }, [leaving, isInternal, clearLeaving])

  if (isLoading) {
    return (
      <Stack justifyContent="center" alignItems="center" height={vhMinus()}>
        <Loader size={64} />
      </Stack>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.SIGN_IN} replace />
  }

  // Gate 1 — is this Vera staff, operating AS Vera staff? VERA-876: while in a
  // company's space, a Vera staffer operates AS a brand user, so internal routes are
  // off-limits and redirect to the brand home. `useInternalRouteAccess` carries that
  // `!inSpace` clause, matching the sidebar (which hides the internal menu in space
  // mode) and the space-mode permission cap.
  if (!isInternal) {
    // Real company users go to `/` — Home, which is permanent (VRG-338). This used to be
    // /certificates; see the redirectPath default above for why that was wrong.
    //
    // In-space Vera staff go where the caller that entered space asked to go, and
    // otherwise to the brand's Settings (view_company-gated, so it loads under any
    // grant). This guard's redirect regularly WINS the race against the enter
    // handler's own `navigate` — it is created during the render that `enter()`
    // triggers, before that navigate commits, and it uses `replace` — so agreeing on
    // the destination is the only way a caller can land somewhere else. Settings was
    // a safe default back when a demo's grant was empty and Products would not have
    // loaded anyway; now that a demo carries product_view, the Demos console's "Add
    // products" needs to actually reach Products (VRG-369).
    if (inSpace) {
      return (
        <Navigate
          to={landing?.path ?? ROUTES.SETTINGS_COMPANY_GENERAL}
          state={landing?.state}
          replace
        />
      )
    }
    return <Navigate to={redirectPath} replace />
  }

  // Gate 2 — may this staffer use THIS page? Hidden rather than shown-and-explained,
  // per §27: a whole internal surface someone cannot reach is not a discoverable action
  // with a path to access, it is a door that does not open. Companies is the fallback
  // because every internal role holds `view_companies` today, so the redirect lands
  // somewhere that actually opens.
  //
  // The `pathname !== fallback` clause is what makes that TERMINATE rather than merely
  // be true today. A role whose `view_companies` is revoked (custom internal roles are
  // editable) would otherwise redirect Companies to Companies forever, and `/` bounces
  // internal users straight back here (`HomePage.tsx`), so the loop would span two
  // components and be invisible in either one. Falling through renders the page and
  // lets its own empty/error state say so, which is a bad outcome rather than a hang.
  if (pathname !== ROUTES.INTERNAL_COMPANIES && !canReachInternalPath(pathname)) {
    return <Navigate to={ROUTES.INTERNAL_COMPANIES} replace />
  }

  return <Outlet />
}
